import React from 'react';
import { css } from 'styled-components';

type Full<T> = {
  [P in keyof T]-?: T[P];
};

export interface TypographyBase {
  htmlFontSize: number;
  fontFamily: string;
  fontWeight: number;
  pxToRem: (px: number | string, baseFontPercentage: number, baseFontPixel: number) => string;
  typographyToCss: (t: TypographyLevel) => ReturnType<typeof css>;
}

type TStyleKeys =
  | 'fontFamily'
  | 'fontWeight'
  | 'fontSize'
  | 'lineHeight'
  | 'letterSpacing'
  | 'textDecoration';

export type TypographyLevel = Full<Pick<React.CSSProperties, TStyleKeys>> & {
  case: 'sentence' | 'all-caps' | 'none';
};

export const typographyLevelsTypes = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'body1',
  'body2',
  'body3',
  'buttonPrimary',
  'buttonText',
  'overline',
  'captionLarge',
  'captionSmall',
] as const;
export type TTypographyLevels = typeof typographyLevelsTypes[number];
export const typographyLevelsList = Array.from(typographyLevelsTypes);

export type TFontCuts<Keys extends string> = Record<Keys, TypographyLevel>;

export type TCutBase = 'default';
export type TH1Variants = TCutBase;
export type TH2Variants = TCutBase;
export type TH3Variants = TCutBase;
export type TH4Variants = TCutBase | 'allCaps' | 'strong';
export type TH5Variants = TCutBase | 'strong';
export type TH6Variants = TCutBase | 'strong';
export type TBody1Variants = TCutBase | 'strong' | 'link';
export type TBody2Variants = TCutBase | 'strong' | 'link';
export type TBody3Variants = TCutBase | 'strong' | 'link';
export type TButtonPrimaryVariants = TCutBase;
export type TButtonTextVariants = TCutBase;
export type TOverlineVariants = TCutBase | 'strong';
export type TCaptionLargeVariants = TCutBase;
export type TCaptionSmallVariants = TCutBase;

export type TH1 = TFontCuts<TH1Variants>;
export type TH2 = TFontCuts<TH2Variants>;
export type TH3 = TFontCuts<TH3Variants>;
export type TH4 = TFontCuts<TH4Variants>;
export type TH5 = TFontCuts<TH5Variants>;
export type TH6 = TFontCuts<TH6Variants>;

export type TBody1 = TFontCuts<TBody1Variants>;
export type TBody2 = TFontCuts<TBody2Variants>;
export type TBody3 = TFontCuts<TBody3Variants>;

export type TButtonPrimary = TFontCuts<TButtonPrimaryVariants>;
export type TButtonText = TFontCuts<TButtonTextVariants>;

export type TOverline = TFontCuts<TOverlineVariants>;
export type TCaptionLarge = TFontCuts<TCaptionLargeVariants>;
export type TCaptionSmall = TFontCuts<TCaptionSmallVariants>;

export type TTypographyVariants = {
  h1: TH1;
  h2: TH2;
  h3: TH3;
  h4: TH4;
  h5: TH5;
  h6: TH6;
  body1: TBody1;
  body2: TBody2;
  body3: TBody3;
  buttonPrimary: TButtonPrimary;
  buttonText: TButtonText;
  overline: TOverline;
  captionLarge: TCaptionLarge;
  captionSmall: TCaptionSmall;
};

export type TTypographyTheme = TypographyBase & TTypographyVariants;
